import React, {
  useCallback, useState, ReactElement, ForwardedRef,
} from 'react';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import DatePicker from '../date-picker/date-picker.component';
import FormatService from '../../services/format-service';
import useStylesFieldDate from './field-date.styles';

const FieldDate: ReactMemoComponent = React.memo(React.forwardRef((
  props: ReactComponentProps,
  ref: ForwardedRef<HTMLDivElement>,
): ReactElement => {
  const FORM_CONTROL_VARIANT_OUTLINED: FormControlVariant = 'outlined';
  const LABEL_VARIANT_OUTLINED: LabelVariant = 'outlined';

  const {
    isPlain,
    isBordered,
    isOutlined,
    isReadOnly,
    withNoUnderline,
    withNoFutureSelection,
    withNoPastSelection,
    withBlankValues,
    value,
    label,
    id,
    errorDeps,
    className,
    labelClassName,
    inputRefs,
    inputRefIndex,
    onChange,
    onError,
    input,
    meta,
    ...custom
  } = props;

  const {
    touched: isTouched,
    invalid: isInvalid,
    error: formError,
    submitError: formSubmitError,
    modifiedSinceLastSubmit,
  } = (meta || {}) as FormFieldMetaProps;

  const { classes } = useStylesFieldDate();

  const borderedClassName: string | undefined = isBordered || isOutlined ? classes.fieldDateBordered : undefined;

  const withErrors: boolean = ((isTouched && isInvalid) || !!formError || !!formSubmitError)
    && !modifiedSinceLastSubmit;

  const [errorMessage, setErrorMessage]: [string | null, Function] = useState(null);

  const onFormError: Function = useCallback((message: string, currentValue: string): void => {
    setErrorMessage(!currentValue && withBlankValues ? null : message);

    if (onError) {
      onError(message, currentValue);
    }
  }, [withBlankValues, ...(errorDeps || [])]);

  return (
    <FormControl
      ref={ref}
      variant={isOutlined ? FORM_CONTROL_VARIANT_OUTLINED : undefined}
      className={FormatService.formatClassNames(classes.fieldDate, borderedClassName, className)}
      error={withErrors}
    >
      {
        !!label && (
          <InputLabel
            shrink
            id={id || undefined}
            className={labelClassName || undefined}
            variant={isOutlined ? LABEL_VARIANT_OUTLINED : undefined}
          >
            {label}
          </InputLabel>
        )
      }
      <DatePicker
        isPlain={isPlain}
        isReadOnly={isReadOnly}
        withNoUnderline={withNoUnderline}
        withNoFutureSelection={withNoFutureSelection}
        withNoPastSelection={withNoPastSelection}
        withBlankValues={withBlankValues}
        value={isPlain ? value : undefined}
        inputRefs={inputRefs}
        inputRefIndex={inputRefIndex}
        input={input}
        error={withErrors && (formError || formSubmitError || errorMessage)}
        errorDeps={errorDeps}
        onChange={onChange}
        onError={onFormError}
        {...(!isPlain ? input : {})}
        {...custom}
      />
    </FormControl>
  );
}));

export default FieldDate;
